import { useTheme } from '../../../lib/theme-context.js';
import { cn } from '../../../lib/utils.js';

/**
 * Shared tag chip. Renders the tag's saved colour as the **chip
 * background** (full opacity, not the previous 20%/40% alpha tint)
 * and the tag's icon (one of the legacy 76 SVGs served from
 * `/img/tags/`) tinted to match the active theme:
 *   • Light theme → icon stays black (legacy SVGs are black-on-
 *     transparent already).
 *   • Dark theme → icon inverts to white via CSS `filter: invert(1)`.
 *
 * Operator-reported: "tags chip background color should reflect
 * what was saved as tag color everywhere, icon should be white for
 * dark theme and black for light theme". The chip text is always
 * white because legacy tag colours run dark-to-saturated; black
 * text on `#0056B2` would be unreadable.
 *
 * Used by:
 *   • `ScenarioRow` — inline tag chips on the scenarios list.
 *   • `ScenarioDetailModal` — Tags summary box.
 *   • `PresentationListPage` — group header + filter pills.
 *   • `ScenarioTagSelector` — popover row preview swatch.
 *
 * Centralising the markup means future tweaks (rounded radius,
 * font weight, hover affordance) land in one place.
 */
export interface TagChipTag {
  title: string;
  color?: string | null;
  icon?: string | null;
}

export interface TagChipProps {
  tag: TagChipTag;
  /** Size preset — `sm` is the inline-list chip (default), `md` is
   *  the larger Tags-box / presentation-header pill. */
  size?: 'sm' | 'md';
  /** Hide the icon (useful for headers where the icon is rendered
   *  separately at a larger size). */
  hideIcon?: boolean;
  className?: string;
  /** Tooltip override. Defaults to `tag.title`. */
  title?: string;
}

const DEFAULT_TAG_BG = '#6b7280'; // tailwind gray-500 — used when a tag has no colour

export function TagChip({
  tag,
  size = 'sm',
  hideIcon,
  className,
  title,
}: TagChipProps): JSX.Element {
  const { resolvedTheme } = useTheme();
  const bg = tag.color || DEFAULT_TAG_BG;
  const sizeCx = size === 'md' ? 'px-2 py-0.5 text-xs gap-1.5' : 'px-1.5 py-0.5 text-[11px] gap-1';
  const iconSizePx = size === 'md' ? 14 : 12;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border border-black/10 font-medium text-white shadow-sm',
        sizeCx,
        className,
      )}
      style={{ backgroundColor: bg }}
      title={title ?? tag.title}
    >
      {!hideIcon && tag.icon ? (
        <img
          src={`/img/tags/${tag.icon}`}
          alt=""
          width={iconSizePx}
          height={iconSizePx}
          // The 76 legacy SVGs are black-on-transparent. Invert in
          // dark mode so they read white against the colourful chip
          // background; keep native black in light mode.
          style={{
            filter: resolvedTheme === 'dark' ? 'invert(1)' : 'none',
            flexShrink: 0,
          }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
          }}
        />
      ) : null}
      <span className="truncate">{tag.title}</span>
    </span>
  );
}
